import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const styles = {
  global: `
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    
    body {
      font-family: 'Outfit', -apple-system, BlinkMacSystemFont, sans-serif;
      background: linear-gradient(135deg, #e8f0e9 0%, #d4e5d8 50%, #c5dde0 100%);
      min-height: 100vh;
      color: #3d4f4f;
    }
    
    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(10px); }
      to { opacity: 1; transform: translateY(0); }
    }
    
    @keyframes float {
      0%, 100% { transform: translateY(0px); }
      50% { transform: translateY(-8px); }
    }
    
    button:hover {
      transform: translateY(-2px);
    }
    button:active {
      transform: translateY(0);
    }
    
    input::placeholder, textarea::placeholder {
      color: #9ab0a5;
    }
    
    input:focus, textarea:focus {
      outline: none;
      border-color: #7c9885;
      background: white;
      box-shadow: 0 0 0 4px rgba(124, 152, 133, 0.1);
    }
  `,
  
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    position: 'relative',
    overflow: 'hidden',
  },
  
  bgCircle1: {
    position: 'absolute',
    width: '400px',
    height: '400px',
    borderRadius: '50%',
    background: 'rgba(124, 152, 133, 0.15)',
    top: '-100px',
    right: '-100px',
    animation: 'float 8s ease-in-out infinite',
  },
  
  bgCircle2: {
    position: 'absolute',
    width: '300px',
    height: '300px',
    borderRadius: '50%',
    background: 'rgba(147, 180, 186, 0.12)',
    bottom: '-80px',
    left: '-80px',
    animation: 'float 10s ease-in-out infinite 1s',
  },
  
  bgCircle3: {
    position: 'absolute',
    width: '150px',
    height: '150px',
    borderRadius: '50%',
    background: 'rgba(200, 180, 160, 0.1)',
    top: '40%',
    left: '10%',
    animation: 'float 6s ease-in-out infinite 0.5s',
  },
  
  mainCard: {
    background: 'rgba(255, 255, 255, 0.85)',
    backdropFilter: 'blur(20px)',
    borderRadius: '24px',
    padding: '32px',
    width: '100%',
    maxWidth: '1100px',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 8px 32px rgba(61, 79, 79, 0.08), 0 2px 8px rgba(61, 79, 79, 0.04)',
    border: '1px solid rgba(255, 255, 255, 0.6)',
    animation: 'fadeIn 0.6s ease-out',
    position: 'relative',
    zIndex: 1,
  },
  
  header: {
    textAlign: 'center',
    marginBottom: '24px',
  },
  
  logoText: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#3d4f4f',
    letterSpacing: '-0.5px',
  },
  
  subtitle: {
    fontSize: '18px',
    color: '#6b8080',
    marginTop: '4px',
  },
  
  contentWrapper: {
    display: 'flex',
    gap: '24px',
  },
  
  formSection: {
    flex: 1,
    minWidth: 0,
  },
  
  previewSection: {
    flex: 1,
    minWidth: 0,
  },
  
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#3d4f4f',
    marginBottom: '16px',
    paddingBottom: '8px',
    borderBottom: '2px solid rgba(124, 152, 133, 0.2)',
  },
  
  formGroup: {
    marginBottom: '16px',
  },
  
  label: {
    display: 'block',
    fontSize: '16px',
    fontWeight: '500',
    color: '#5a7070',
    marginBottom: '6px',
  },
  
  input: {
    width: '100%',
    padding: '12px 14px',
    border: '2px solid rgba(124, 152, 133, 0.2)',
    borderRadius: '10px',
    fontSize: '16px',
    color: '#3d4f4f',
    background: 'rgba(255, 255, 255, 0.8)',
    transition: 'all 0.3s ease',
    fontFamily: 'inherit',
  },
  
  inputSmall: {
    width: '100%',
    padding: '10px 12px',
    border: '2px solid rgba(124, 152, 133, 0.2)',
    borderRadius: '8px',
    fontSize: '15px',
    color: '#3d4f4f',
    background: 'rgba(255, 255, 255, 0.8)',
    transition: 'all 0.3s ease',
    fontFamily: 'inherit',
  },
  
  textarea: {
    width: '100%',
    padding: '12px 14px',
    border: '2px solid rgba(124, 152, 133, 0.2)',
    borderRadius: '10px',
    fontSize: '16px',
    color: '#3d4f4f',
    background: 'rgba(255, 255, 255, 0.8)',
    transition: 'all 0.3s ease',
    fontFamily: 'inherit',
    resize: 'vertical',
    minHeight: '80px',
  },
  
  row: {
    display: 'flex',
    gap: '12px',
  },
  
  halfWidth: {
    flex: 1,
  },
  
  roleEducationItem: {
    background: 'rgba(124, 152, 133, 0.05)',
    borderRadius: '12px',
    padding: '12px',
    marginBottom: '12px',
    position: 'relative',
  },
  
  itemHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  
  itemTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#7c9885',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  
  removeButton: {
    background: 'rgba(180, 120, 120, 0.15)',
    border: 'none',
    borderRadius: '6px',
    padding: '4px 10px',
    fontSize: '12px',
    color: '#8a5a5a',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontFamily: 'inherit',
  },
  
  addButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    padding: '12px',
    border: '2px dashed rgba(124, 152, 133, 0.3)',
    borderRadius: '12px',
    background: 'transparent',
    color: '#7c9885',
    fontSize: '15px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    fontFamily: 'inherit',
    marginBottom: '12px',
  },
  
  charCount: {
    fontSize: '14px',
    color: '#9ab0a5',
    textAlign: 'right',
    marginTop: '4px',
  },
  
  charCountWarning: {
    color: '#b47878',
  },
  
  // Preview styles
  cvPreview: {
    background: 'white',
    borderRadius: '12px',
    padding: '24px',
    minHeight: '500px',
    boxShadow: '0 2px 8px rgba(61, 79, 79, 0.06)',
    border: '1px solid rgba(124, 152, 133, 0.1)',
  },
  
  cvHeader: {
    borderBottom: '2px solid #7c9885',
    paddingBottom: '16px',
    marginBottom: '16px',
  },
  
  cvName: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#3d4f4f',
    marginBottom: '4px',
  },
  
  cvMeta: {
    fontSize: '16px',
    color: '#6b8080',
  },
  
  cvSection: {
    marginBottom: '20px',
  },
  
  cvSectionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#7c9885',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '10px',
    paddingBottom: '4px',
    borderBottom: '1px solid rgba(124, 152, 133, 0.2)',
  },
  
  cvSummary: {
    fontSize: '16px',
    color: '#5a7070',
    lineHeight: '1.6',
    fontStyle: 'italic',
  },
  
  cvItem: {
    marginBottom: '12px',
  },
  
  cvItemTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#3d4f4f',
  },
  
  cvItemSubtitle: {
    fontSize: '15px',
    color: '#6b8080',
  },
  
  cvItemMeta: {
    fontSize: '14px',
    color: '#9ab0a5',
  },
  
  cvEmpty: {
    color: '#bdc8c4',
    fontStyle: 'italic',
    fontSize: '15px',
  },
  
  // Button styles
  buttonContainer: {
    marginTop: '20px',
    display: 'flex',
    gap: '12px',
  },
  
  button: {
    padding: '14px 24px',
    border: 'none',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    fontFamily: 'inherit',
  },
  
  buttonPrimary: {
    background: 'linear-gradient(135deg, #7c9885 0%, #6a8a74 100%)',
    color: 'white',
    boxShadow: '0 4px 12px rgba(124, 152, 133, 0.35)',
  },
  
  buttonSecondary: {
    background: 'rgba(124, 152, 133, 0.1)',
    color: '#5a7070',
  },
  
  message: {
    padding: '12px 16px',
    borderRadius: '10px',
    fontSize: '16px',
    textAlign: 'center',
    marginBottom: '16px',
  },
  
  messageError: {
    background: 'rgba(180, 120, 120, 0.12)',
    color: '#8a5a5a',
    border: '1px solid rgba(180, 120, 120, 0.2)',
  },
  
  messageSuccess: {
    background: 'rgba(124, 152, 133, 0.12)',
    color: '#5a7a65',
    border: '1px solid rgba(124, 152, 133, 0.2)',
  },

  // Upload section styles
  uploadSection: {
    marginBottom: '24px',
    padding: '24px',
    background: 'linear-gradient(135deg, rgba(124, 152, 133, 0.08) 0%, rgba(147, 180, 186, 0.08) 100%)',
    borderRadius: '16px',
    border: '2px dashed rgba(124, 152, 133, 0.3)',
    textAlign: 'center',
  },

  uploadSectionActive: {
    borderColor: '#7c9885',
    background: 'rgba(124, 152, 133, 0.15)',
  },

  uploadIcon: {
    fontSize: '48px',
    marginBottom: '12px',
  },

  uploadTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#3d4f4f',
    marginBottom: '8px',
  },

  uploadSubtitle: {
    fontSize: '14px',
    color: '#6b8080',
    marginBottom: '16px',
  },

  uploadButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #7c9885 0%, #5a7a65 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    fontFamily: 'inherit',
    boxShadow: '0 4px 12px rgba(124, 152, 133, 0.35)',
  },

  uploadInput: {
    display: 'none',
  },

  uploadFormats: {
    fontSize: '12px',
    color: '#9ab0a5',
    marginTop: '12px',
  },

  uploadProgress: {
    marginTop: '16px',
    padding: '12px',
    background: 'rgba(255, 255, 255, 0.8)',
    borderRadius: '10px',
  },

  progressBar: {
    height: '8px',
    background: 'rgba(124, 152, 133, 0.2)',
    borderRadius: '4px',
    overflow: 'hidden',
    marginBottom: '8px',
  },

  progressFill: {
    height: '100%',
    background: 'linear-gradient(135deg, #7c9885 0%, #5a7a65 100%)',
    borderRadius: '4px',
    transition: 'width 0.3s ease',
  },

  progressText: {
    fontSize: '13px',
    color: '#5a7070',
    textAlign: 'center',
  },

  dividerOr: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '24px',
  },

  dividerLine: {
    flex: 1,
    height: '1px',
    background: 'rgba(124, 152, 133, 0.2)',
  },

  dividerText: {
    fontSize: '14px',
    color: '#9ab0a5',
    fontWeight: '500',
  },

  uploadedFile: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px',
    background: 'rgba(124, 152, 133, 0.1)',
    borderRadius: '12px',
    marginTop: '16px',
  },

  uploadedFileIcon: {
    fontSize: '32px',
  },

  uploadedFileInfo: {
    flex: 1,
  },

  uploadedFileName: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#3d4f4f',
    marginBottom: '2px',
  },

  uploadedFileMeta: {
    fontSize: '12px',
    color: '#6b8080',
  },

  uploadedFileActions: {
    display: 'flex',
    gap: '8px',
  },

  viewButton: {
    padding: '8px 16px',
    background: 'linear-gradient(135deg, #7c9885 0%, #5a7a65 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.2s ease',
  },

  deleteButton: {
    padding: '8px 16px',
    background: 'rgba(180, 120, 120, 0.15)',
    color: '#8a5a5a',
    border: 'none',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.2s ease',
  },

  // CV Viewer Modal
  cvViewerOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px',
  },

  cvViewerModal: {
    background: 'white',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '900px',
    height: '90vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
  },

  cvViewerHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderBottom: '1px solid rgba(124, 152, 133, 0.2)',
    background: 'rgba(124, 152, 133, 0.05)',
  },

  cvViewerTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#3d4f4f',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },

  cvViewerActions: {
    display: 'flex',
    gap: '10px',
  },

  cvViewerCloseBtn: {
    background: 'rgba(180, 120, 120, 0.15)',
    border: 'none',
    borderRadius: '8px',
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#8a5a5a',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },

  cvViewerDownloadBtn: {
    background: 'linear-gradient(135deg, #7c9885 0%, #5a7a65 100%)',
    border: 'none',
    borderRadius: '8px',
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: '600',
    color: 'white',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },

  cvViewerContent: {
    flex: 1,
    overflow: 'hidden',
  },

  cvViewerIframe: {
    width: '100%',
    height: '100%',
    border: 'none',
  },
};

function CreateCvPage({ user, setUser }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  
  // Upload state
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragOver, setIsDragOver] = useState(false);
  const [cvPreviewUrl, setCvPreviewUrl] = useState(null);
  const [showCvViewer, setShowCvViewer] = useState(false);
  
  // Form state - start with just one entry each
  const [age, setAge] = useState('');
  const [nationality, setNationality] = useState('');
  const [summary, setSummary] = useState('');
  const [roles, setRoles] = useState([{ title: '', company: '', years: '' }]);
  const [education, setEducation] = useState([{ degree: '', institution: '', year: '' }]);

  // Fetch signed URL for CV preview
  const fetchCvPreviewUrl = async () => {
    console.log('fetchCvPreviewUrl called');
    console.log('user.cvFile:', user.cvFile);
    console.log('user._id:', user._id);
    
    if (!user.cvFile) {
      console.log('No cvFile, returning early');
      return;
    }
    
    try {
      const url = `${API_URL}/auth/cv/${user._id}`;
      console.log('Fetching from:', url);
      
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      console.log('Response status:', response.status);
      const data = await response.json();
      console.log('Response data:', data);
      
      if (data.success) {
        console.log('Setting cvPreviewUrl to:', data.data.url);
        setCvPreviewUrl(data.data.url);
      } else {
        console.log('Response not successful:', data.message);
      }
    } catch (error) {
      console.error('Error fetching CV URL:', error);
    }
  };

  // Load existing CV data if available
  useEffect(() => {
    if (user.cv) {
      setAge(user.cv.age || '');
      setNationality(user.cv.nationality || '');
      setSummary(user.cv.summary || '');
      
      if (user.cv.roles && user.cv.roles.length > 0) {
        setRoles(user.cv.roles);
      }
      
      if (user.cv.education && user.cv.education.length > 0) {
        setEducation(user.cv.education);
      }
    }
  }, [user.cv]);

  // Fetch CV preview URL when user has a CV file
  useEffect(() => {
    if (user.cvFile) {
      fetchCvPreviewUrl();
    } else {
      setCvPreviewUrl(null);
    }
  }, [user.cvFile]);

  // Handle file upload
  const handleFileUpload = async (file) => {
    console.log('handleFileUpload called with:', file);
    
    if (!file) {
      console.log('No file provided');
      return;
    }

    console.log('File type:', file.type);
    console.log('File size:', file.size);

    // Validate file type - PDF only
    if (file.type !== 'application/pdf') {
      console.log('Invalid file type');
      setMessage({ text: 'Please upload a PDF file (.pdf)', type: 'error' });
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      console.log('File too large');
      setMessage({ text: 'File size must be less than 10MB', type: 'error' });
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setMessage({ text: '', type: '' });

    try {
      const formData = new FormData();
      formData.append('cvFile', file);

      console.log('Sending request to:', `${API_URL}/auth/upload-cv`);
      console.log('Token:', localStorage.getItem('token') ? 'exists' : 'missing');

      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 100);

      const response = await fetch(`${API_URL}/auth/upload-cv`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      console.log('Response received');
      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);

      clearInterval(progressInterval);
      setUploadProgress(100);

      const data = await response.json();
      console.log('Response data:', data);

      if (data.success) {
        setUser(data.data.user);
        setMessage({ text: 'CV uploaded successfully! Recruiters will be able to view it when you match.', type: 'success' });
        // Fetch the preview URL for the newly uploaded CV
        if (data.data.user.cvFile && data.data.user.cvFile.s3Key) {
          try {
            const urlResponse = await fetch(`${API_URL}/auth/cv/${data.data.user._id}`, {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`
              }
            });
            const urlData = await urlResponse.json();
            if (urlData.success) {
              setCvPreviewUrl(urlData.data.url);
            }
          } catch (err) {
            console.error('Error fetching CV preview URL:', err);
          }
        }
      } else {
        setMessage({ text: data.message || 'Failed to upload CV. Please try again.', type: 'error' });
      }
    } catch (error) {
      console.error('Upload error:', error);
      console.error('Error name:', error.name);
      console.error('Error message:', error.message);
      setMessage({ text: 'Failed to upload CV. Please try again.', type: 'error' });
    } finally {
      setUploading(false);
      setTimeout(() => setUploadProgress(0), 1000);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    handleFileUpload(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    handleFileUpload(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDeleteCv = async () => {
    if (!window.confirm('Are you sure you want to delete your uploaded CV?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/auth/delete-cv`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setUser(data.data.user);
        setMessage({ text: 'CV deleted successfully.', type: 'success' });
      } else {
        setMessage({ text: data.message || 'Failed to delete CV.', type: 'error' });
      }
    } catch (error) {
      console.error('Delete error:', error);
      setMessage({ text: 'Failed to delete CV. Please try again.', type: 'error' });
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const updateRole = (index, field, value) => {
    const newRoles = [...roles];
    newRoles[index][field] = value;
    setRoles(newRoles);
  };

  const addRole = () => {
    setRoles([...roles, { title: '', company: '', years: '' }]);
  };

  const removeRole = (index) => {
    if (roles.length > 1) {
      setRoles(roles.filter((_, i) => i !== index));
    }
  };

  const updateEducation = (index, field, value) => {
    const newEducation = [...education];
    newEducation[index][field] = value;
    setEducation(newEducation);
  };

  const addEducation = () => {
    setEducation([...education, { degree: '', institution: '', year: '' }]);
  };

  const removeEducation = (index) => {
    if (education.length > 1) {
      setEducation(education.filter((_, i) => i !== index));
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setMessage({ text: '', type: '' });

    // Filter out empty roles and education
    const filledRoles = roles.filter(r => r.title || r.company || r.years);
    const filledEducation = education.filter(e => e.degree || e.institution || e.year);

    const cvData = {
      age: age ? parseInt(age) : null,
      nationality,
      summary,
      roles: filledRoles,
      education: filledEducation,
    };

    try {
      const response = await fetch(`${API_URL}/auth/update`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ cv: cvData, signUpFinished: true })
      });

      const data = await response.json();

      if (data.success) {
        setUser(data.data.user);
        setMessage({ text: 'CV saved successfully!', type: 'success' });
        setTimeout(() => navigate('/home'), 1500);
      } else {
        setMessage({ text: data.message, type: 'error' });
      }
    } catch (error) {
      setMessage({ text: 'Unable to save CV', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Filter for preview (only show filled items)
  const filledRoles = roles.filter(r => r.title || r.company);
  const filledEducation = education.filter(e => e.degree || e.institution);

  return (
    <>
      <style>{styles.global}</style>
      
      <div style={styles.container}>
        <div style={styles.bgCircle1}></div>
        <div style={styles.bgCircle2}></div>
        <div style={styles.bgCircle3}></div>
        
        <div style={styles.mainCard}>
          <div style={styles.header}>
            <h1 style={styles.logoText}>Instaview</h1>
            <p style={styles.subtitle}>Create your CV, {user.firstName}</p>
          </div>

          {message.text && (
            <div style={{
              ...styles.message,
              ...(message.type === 'error' ? styles.messageError : styles.messageSuccess)
            }}>
              {message.text}
            </div>
          )}

          {/* Upload Section */}
          <div 
            style={{
              ...styles.uploadSection,
              ...(isDragOver ? styles.uploadSectionActive : {})
            }}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            <div style={styles.uploadIcon}>📄</div>
            <div style={styles.uploadTitle}>Upload your CV</div>
            <div style={styles.uploadSubtitle}>
              {user.cvFile ? 'Replace your current CV or keep it' : 'Recruiters will be able to view your CV when you match'}
            </div>
            
            <label>
              <input
                type="file"
                accept=".pdf"
                onChange={handleFileSelect}
                style={styles.uploadInput}
                disabled={uploading}
              />
              <span style={{
                ...styles.uploadButton,
                opacity: uploading ? 0.7 : 1,
                cursor: uploading ? 'not-allowed' : 'pointer'
              }}>
                {uploading ? '⏳ Uploading...' : user.cvFile ? '📁 Replace CV' : '📁 Choose File'}
              </span>
            </label>

            {uploading && (
              <div style={styles.uploadProgress}>
                <div style={styles.progressBar}>
                  <div style={{...styles.progressFill, width: `${uploadProgress}%`}}></div>
                </div>
                <div style={styles.progressText}>
                  {uploadProgress < 100 ? 'Uploading your CV...' : 'Done!'}
                </div>
              </div>
            )}

            {/* Show uploaded file info and preview */}
            {user.cvFile && !uploading && (
              <>
                {console.log('user.cvFile:', user.cvFile)}
                {console.log('cvPreviewUrl:', cvPreviewUrl)}
                <div style={styles.uploadedFile}>
                  <div style={styles.uploadedFileIcon}>📕</div>
                  <div style={styles.uploadedFileInfo}>
                    <div style={styles.uploadedFileName}>{user.cvFile.originalName}</div>
                    <div style={styles.uploadedFileMeta}>
                      {formatFileSize(user.cvFile.size)} • Uploaded {new Date(user.cvFile.uploadedAt).toLocaleDateString()}
                    </div>
                  </div>
                  <div style={styles.uploadedFileActions}>
                    <button onClick={handleDeleteCv} style={styles.deleteButton}>
                      🗑️ Delete
                    </button>
                  </div>
                </div>
                {cvPreviewUrl && (
                  <div style={{
                    marginTop: '16px',
                    background: 'white',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    border: '1px solid rgba(124, 152, 133, 0.2)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                  }}>
                    <iframe
                      src={`${cvPreviewUrl}#view=FitH&toolbar=0&navpanes=0`}
                      style={{
                        width: '100%',
                        height: '400px',
                        border: 'none',
                        display: 'block',
                      }}
                      title="CV Preview"
                    />
                  </div>
                )}
              </>
            )}

            <div style={styles.uploadFormats}>
              Supported format: PDF (max 10MB)
            </div>
          </div>

          {/* Divider */}
          <div style={styles.dividerOr}>
            <div style={styles.dividerLine}></div>
            <span style={styles.dividerText}>and/or fill in details below</span>
            <div style={styles.dividerLine}></div>
          </div>

          <div style={styles.contentWrapper}>
            {/* Left side - Form */}
            <div style={styles.formSection}>
              <h2 style={styles.sectionTitle}>Your Information</h2>
              
              <div style={styles.row}>
                <div style={styles.halfWidth}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Age</label>
                    <input
                      type="number"
                      value={age}
                      onChange={(e) => setAge(e.target.value)}
                      style={styles.input}
                      placeholder="25"
                      min="16"
                      max="100"
                    />
                  </div>
                </div>
                <div style={styles.halfWidth}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Nationality</label>
                    <input
                      type="text"
                      value={nationality}
                      onChange={(e) => setNationality(e.target.value)}
                      style={styles.input}
                      placeholder="Swedish"
                    />
                  </div>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Professional Summary</label>
                <textarea
                  value={summary}
                  onChange={(e) => setSummary(e.target.value.slice(0, 250))}
                  style={styles.textarea}
                  placeholder="Brief summary of your professional background and goals..."
                  maxLength={250}
                />
                <div style={{
                  ...styles.charCount,
                  ...(summary.length > 230 ? styles.charCountWarning : {})
                }}>
                  {summary.length}/250
                </div>
              </div>

              <h3 style={{...styles.sectionTitle, marginTop: '24px'}}>Work Experience</h3>
              {roles.map((role, index) => (
                <div key={index} style={styles.roleEducationItem}>
                  <div style={styles.itemHeader}>
                    <span style={styles.itemTitle}>Role {index + 1}</span>
                    {roles.length > 1 && (
                      <button
                        onClick={() => removeRole(index)}
                        style={styles.removeButton}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <div style={styles.row}>
                    <div style={styles.halfWidth}>
                      <input
                        type="text"
                        value={role.title}
                        onChange={(e) => updateRole(index, 'title', e.target.value)}
                        style={styles.inputSmall}
                        placeholder="Job Title"
                      />
                    </div>
                    <div style={styles.halfWidth}>
                      <input
                        type="text"
                        value={role.company}
                        onChange={(e) => updateRole(index, 'company', e.target.value)}
                        style={styles.inputSmall}
                        placeholder="Company"
                      />
                    </div>
                  </div>
                  <div style={{marginTop: '8px'}}>
                    <input
                      type="text"
                      value={role.years}
                      onChange={(e) => updateRole(index, 'years', e.target.value)}
                      style={styles.inputSmall}
                      placeholder="Years (e.g., 2020-2023)"
                    />
                  </div>
                </div>
              ))}
              <button
                onClick={addRole}
                style={styles.addButton}
                onMouseOver={(e) => {
                  e.currentTarget.style.borderColor = '#7c9885';
                  e.currentTarget.style.background = 'rgba(124, 152, 133, 0.05)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(124, 152, 133, 0.3)';
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                <span style={{ fontSize: '20px' }}>+</span> Add Experience
              </button>

              <h3 style={{...styles.sectionTitle, marginTop: '24px'}}>Education</h3>
              {education.map((edu, index) => (
                <div key={index} style={styles.roleEducationItem}>
                  <div style={styles.itemHeader}>
                    <span style={styles.itemTitle}>Education {index + 1}</span>
                    {education.length > 1 && (
                      <button
                        onClick={() => removeEducation(index)}
                        style={styles.removeButton}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <div style={styles.row}>
                    <div style={styles.halfWidth}>
                      <input
                        type="text"
                        value={edu.degree}
                        onChange={(e) => updateEducation(index, 'degree', e.target.value)}
                        style={styles.inputSmall}
                        placeholder="Degree"
                      />
                    </div>
                    <div style={styles.halfWidth}>
                      <input
                        type="text"
                        value={edu.institution}
                        onChange={(e) => updateEducation(index, 'institution', e.target.value)}
                        style={styles.inputSmall}
                        placeholder="Institution"
                      />
                    </div>
                  </div>
                  <div style={{marginTop: '8px'}}>
                    <input
                      type="text"
                      value={edu.year}
                      onChange={(e) => updateEducation(index, 'year', e.target.value)}
                      style={styles.inputSmall}
                      placeholder="Year (e.g., 2019)"
                    />
                  </div>
                </div>
              ))}
              <button
                onClick={addEducation}
                style={styles.addButton}
                onMouseOver={(e) => {
                  e.currentTarget.style.borderColor = '#7c9885';
                  e.currentTarget.style.background = 'rgba(124, 152, 133, 0.05)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(124, 152, 133, 0.3)';
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                <span style={{ fontSize: '20px' }}>+</span> Add Education
              </button>

              <div style={styles.buttonContainer}>
                <button
                  onClick={handleSave}
                  disabled={loading}
                  style={{ ...styles.button, ...styles.buttonPrimary }}
                >
                  {loading ? 'Saving...' : 'Save CV'}
                </button>
                {user.signUpFinished && (
                  <button
                    onClick={() => navigate('/home')}
                    style={{ ...styles.button, ...styles.buttonSecondary }}
                  >
                    Back to Home
                  </button>
                )}
              </div>
            </div>

            {/* Right side - Preview */}
            <div style={styles.previewSection}>
              <h2 style={styles.sectionTitle}>CV Preview</h2>
              
              <div style={styles.cvPreview}>
                <div style={styles.cvHeader}>
                  <div style={styles.cvName}>
                    {user.firstName} {user.lastName}
                  </div>
                  <div style={styles.cvMeta}>
                    {[age && `${age} years old`, nationality].filter(Boolean).join(' • ') || (
                      <span style={styles.cvEmpty}>Age and nationality will appear here</span>
                    )}
                  </div>
                </div>

                {summary ? (
                  <div style={styles.cvSection}>
                    <div style={styles.cvSectionTitle}>Summary</div>
                    <div style={styles.cvSummary}>{summary}</div>
                  </div>
                ) : (
                  <div style={styles.cvSection}>
                    <div style={styles.cvSectionTitle}>Summary</div>
                    <div style={styles.cvEmpty}>Your professional summary will appear here...</div>
                  </div>
                )}

                <div style={styles.cvSection}>
                  <div style={styles.cvSectionTitle}>Experience</div>
                  {filledRoles.length > 0 ? (
                    filledRoles.map((role, index) => (
                      <div key={index} style={styles.cvItem}>
                        <div style={styles.cvItemTitle}>{role.title || 'Job Title'}</div>
                        <div style={styles.cvItemSubtitle}>{role.company || 'Company'}</div>
                        {role.years && <div style={styles.cvItemMeta}>{role.years}</div>}
                      </div>
                    ))
                  ) : (
                    <div style={styles.cvEmpty}>Add your work experience...</div>
                  )}
                </div>

                <div style={styles.cvSection}>
                  <div style={styles.cvSectionTitle}>Education</div>
                  {filledEducation.length > 0 ? (
                    filledEducation.map((edu, index) => (
                      <div key={index} style={styles.cvItem}>
                        <div style={styles.cvItemTitle}>{edu.degree || 'Degree'}</div>
                        <div style={styles.cvItemSubtitle}>{edu.institution || 'Institution'}</div>
                        {edu.year && <div style={styles.cvItemMeta}>{edu.year}</div>}
                      </div>
                    ))
                  ) : (
                    <div style={styles.cvEmpty}>Add your education...</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default CreateCvPage;